import { useEffect, useState } from 'react'

const SCRIPT_SRC = 'https://chat-assets.frontapp.com/v1/chat.bundle.js'

type FrontChatParams = {
	chatId?: string
	email?: string
	userHash?: string
	onInitCompleted?: () => void
	clearSession?: boolean
	[key: string]: unknown
}

type FrontChat = (cmdType: string, params?: FrontChatParams) => void

export type FrontChatIdentity = {
	email: string
	userHash: string
}

declare global {
	interface Window {
		FrontChat?: FrontChat
	}
}

/** Tracks the identity key currently bound to the live widget (module-level for Strict Mode). */
let activeIdentityKey: string | null = null

const identityKey = (identity?: FrontChatIdentity | null) =>
	identity ? `${identity.email}:${identity.userHash}` : 'anonymous'

const loadFrontChat = (): Promise<FrontChat> => {
	if (typeof window === 'undefined') {
		return Promise.reject(
			new Error('Front Chat is only available in the browser'),
		)
	}

	if (window.FrontChat) {
		return Promise.resolve(window.FrontChat)
	}

	return new Promise((resolve, reject) => {
		const existingScript = document.querySelector<HTMLScriptElement>(
			`script[src="${SCRIPT_SRC}"]`,
		)

		if (existingScript) {
			existingScript.addEventListener('load', () => {
				if (window.FrontChat) {
					resolve(window.FrontChat)
					return
				}

				reject(new Error('[front-chat] Could not set up window.FrontChat'))
			})
			return
		}

		const script = document.createElement('script')
		script.src = SCRIPT_SRC
		script.type = 'text/javascript'
		script.onload = () => {
			if (!window.FrontChat) {
				reject(new Error('[front-chat] Could not set up window.FrontChat'))
				return
			}

			resolve(window.FrontChat)
		}
		document.body.appendChild(script)
	})
}

export const useFrontChat = (
	chatId: string,
	options?: {
		/** Skip init while prerequisites (e.g. auth) are still loading */
		enabled?: boolean
		/** Verified identity — must be passed to `init`, not a separate command */
		identity?: FrontChatIdentity | null
	},
) => {
	const [frontChat, setFrontChat] = useState<FrontChat>()
	const enabled = options?.enabled ?? true
	const email = options?.identity?.email
	const userHash = options?.identity?.userHash

	useEffect(() => {
		if (!chatId || !enabled) return

		let cancelled = false
		const identity = email && userHash ? { email, userHash } : null
		const nextKey = identityKey(identity)

		loadFrontChat()
			.then((api) => {
				if (cancelled) return

				if (activeIdentityKey === nextKey) {
					setFrontChat(() => api)
					return
				}

				const init = () => {
					api('init', {
						chatId,
						...(identity
							? { email: identity.email, userHash: identity.userHash }
							: {}),
						onInitCompleted: () => {
							if (cancelled) return
							activeIdentityKey = nextKey
							setFrontChat(() => api)
						},
					})
				}

				if (activeIdentityKey !== null) {
					api('shutdown', { clearSession: true })
					activeIdentityKey = null
					setFrontChat(undefined)
				}

				init()
			})
			.catch(console.error)

		return () => {
			cancelled = true
		}
	}, [chatId, enabled, email, userHash])

	return { frontChat }
}
